/**
 * iOS status-bar overlap — the one case where the app has to reserve space for
 * it by hand.
 *
 * The PWA declares `apple-mobile-web-app-status-bar-style: black-translucent`,
 * which lets the web view own the whole screen with the status bar drawn on top
 * of it. iOS then compensates by itself — but only on devices with a notch /
 * Dynamic Island, where it lays the page out below the sensor housing. On a
 * plain rectangular screen (every iPad, and iPhones up to the SE) there is
 * nothing to inset around, so the web view really does start at y=0 and the app
 * scrolls visibly behind the clock and the battery.
 *
 * The usual answer, `viewport-fit=cover` + `env(safe-area-inset-top)`, was tried
 * and reverted: cover also redefines `vh` as the full screen, and the ~37 modal
 * cards size themselves in vh (80–92), so on a notched iPhone every modal grew
 * by ~9% and hung off the screen. Fixing a 20px strip on one device class is not
 * worth changing what `vh` means everywhere.
 *
 * So we detect the overlap instead of asking CSS for it: if we are an iOS
 * home-screen web app AND the viewport is exactly as tall as the screen, then
 * nothing was inset for us and the status bar is sitting on our content.
 *
 * Deliberately NOT user-agent sniffing — this measures the actual condition, so
 * it stays correct for devices that do not exist yet.
 */

import { blendHex } from "../utils";

/** iPad and pre-notch iPhone status bar. 20pt on those devices; newer iPads use
 *  24pt, but the headers add their own 10px padding on top of this, so content
 *  clears either one and the strip itself is painted by the header background. */
const IOS_STATUS_BAR_HEIGHT = 20;

/** Rounding slack: iOS reports fractional viewport heights on some devices. */
const TOLERANCE = 2;

/**
 * Pure core, so the branches can be tested without a browser.
 *
 * @param {boolean} standalone   - navigator.standalone (iOS home-screen app)
 * @param {number}  innerHeight  - window.innerHeight
 * @param {number}  screenWidth  - window.screen.width
 * @param {number}  screenHeight - window.screen.height
 * @returns {number} px to reserve above the top edge (0 when iOS already did it)
 */
export function computeStatusBarInset(standalone, innerHeight, screenWidth, screenHeight) {
  if (!standalone) return 0;
  if (!innerHeight || !screenWidth || !screenHeight) return 0;
  // Compare against both screen dimensions: iOS keeps screen.width/height in
  // portrait terms regardless of the current orientation, so a landscape iPad
  // matches screen.width, not screen.height.
  const fullScreen = Math.abs(innerHeight - screenHeight) <= TOLERANCE
    || Math.abs(innerHeight - screenWidth) <= TOLERANCE;
  return fullScreen ? IOS_STATUS_BAR_HEIGHT : 0;
}

/**
 * px to reserve above anything pinned to the top edge of the screen. 0 in every
 * browser tab, on Android, and on notched iPhones (where iOS insets the layout
 * itself). Read it at render time rather than caching at module load — boot
 * runs before the PWA viewport has settled.
 */
export function statusBarInset() {
  if (typeof window === "undefined") return 0;
  return computeStatusBarInset(
    window.navigator?.standalone === true,
    window.innerHeight,
    window.screen?.width,
    window.screen?.height,
  );
}

/* ==========================================================================
 * The OTHER half: the colour iOS paints BEHIND the status bar.
 *
 * On a notched iPhone iOS insets the layout itself (so `statusBarInset()` is
 * 0) and fills the strip above our viewport with a colour it takes from the
 * top of the page. The same colour tints Safari's chrome.
 *
 * THE BUG: after a modal closed, the strip stayed `#8c8c8c` — 45% black
 * (`ModalBackdrop`'s dim) over the white header, measured off a screenshot.
 * Scrolling, tab switches and re-renders never cleared it; only backgrounding
 * the app and returning did.
 *
 * WHAT IT IS NOT. Six rounds of timing fixes — deferred scroll-lock release,
 * theme-color rewrites, single and repeated repaints, a permanent composited
 * layer via `opacity: 0.999` — all failed. Safari 26 does not snapshot the page
 * and does not read `theme-color` at all: it reads the `background-color` of
 * `position: fixed` / `sticky` elements in a thin zone at the viewport edge.
 * A probe panel on the device then measured the four rules that matter:
 *
 *   1. Only OPAQUE content counts. The 45%-black dim never reaches the strip,
 *      which is why a dimmed app could sit under a white strip.
 *   2. The element must be ~8px tall or more (4px does nothing).
 *   3. Among candidates the highest `z-index` wins, ties going to the later one
 *      in the DOM. The app header is `position: fixed; top: 0` with `z-index:
 *      50`, so it is a candidate for the same sample — and it beat a bar at
 *      `z-index: 1` inside the backdrop, which is why the strip stayed white
 *      at the top of the page but worked once the header collapsed away.
 *   4. **The colour is re-read when a qualifying element is INSERTED, not when
 *      an existing one is restyled.** This is the whole original bug: opening a
 *      modal inserts a backdrop (re-read → grey), closing it only removes one
 *      (no re-read → the grey stays, forever).
 *
 * THE FIX: one bar, always mounted, that owns the strip's colour — the header
 * colour normally, the dim composited onto it while a modal is open. Rule 4 is
 * why it is REMOUNTED rather than restyled on every change (see the `key` in
 * `StatusBarTintBar`), and rule 3 is why it is at 2500 and portalled to
 * `<body>`. Always mounted, because a bar that came and went with the modal
 * left no candidate behind on a scrolled page, where the collapsed header no
 * longer qualifies — and iOS kept the dim.
 * ========================================================================= */

/** Height of the bar iOS samples. Measured on an iPhone: an opaque bar of 8px
 *  tints the strip and one of 4px does not (WebKit's own test parameters name a
 *  5px minimum). 10 is the first value on the spacing scale above that, and it
 *  is exactly the app header's top padding — so the bar covers padding, never a
 *  logo, a score or a name. */
const SAMPLE_BAND_PX = 10;

/**
 * The style of the bar that owns the strip's colour, or `null` off iOS.
 *
 * Opaque, 10px tall and at `z-index: 2500` — the three properties the header
 * comment above derives from the on-device measurements. `pointerEvents: none`
 * because the bar sits over the header's top padding and must not eat taps;
 * WebKit samples it regardless (its hit-testing deliberately ignores the
 * property).
 *
 * @param {string} headerBg - the colour the surface paints behind the bar
 * @param {number} dim      - the open modal's black alpha, 0 when none is open
 * @param {boolean} appleTouch - `isAppleTouchDevice()`; null elsewhere, because
 *   no other engine samples the page like this
 */
export function statusBarTintStyle(headerBg, dim, appleTouch) {
  if (!appleTouch) return null;
  return {
    position: "fixed", top: 0, left: 0, right: 0, height: SAMPLE_BAND_PX,
    background: dim ? blendHex("#000000", headerBg, dim) : headerBg,
    // Never eat a tap meant for the header or a backdrop.
    pointerEvents: "none",
    zIndex: 2500,
  };
}

/** iOS/iPadOS, any browser — Safari is WebKit there by definition, and so is
 *  the home-screen app. */
export function isAppleTouchDevice() {
  if (typeof navigator === "undefined") return false;
  if (/iPad|iPhone|iPod/.test(navigator.userAgent || "")) return true;
  // iPadOS 13+ reports a desktop UA by default; the touch count is what still
  // separates it from a Mac, where `maxTouchPoints` is 0.
  return navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
}
